import React, { useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../../utils/cn";
import {
  buildClientSlideRow,
  initialClientSlideIndex,
  type MobileTimelineRow,
} from "../../mobileConsultantAgendaModel";
import { AGENDA_FEATURE_FLAGS } from "../../constants/agendaFeatureFlags";
import { MOBILE_SCHEDULE_THEME } from "../../scheduleGridTheme";
import type { ScheduleVisitOutcome } from "../../../../services/scheduleVisitOutcome";

type MobileScheduleItemProps = {
  row: MobileTimelineRow;
  onPressVisit: (coord: string, scheduleId?: number) => void;
  onPressFreeSlot: (coord: string) => void;
  onUnlockSlot?: (coord: string) => void;
  /** Without scheduleIds every visit of the row is marked (single-client slots). */
  onMarkOutcome: (
    row: MobileTimelineRow,
    outcome: ScheduleVisitOutcome,
    scheduleIds?: number[]
  ) => Promise<void>;
};

function isMultiVisit(row: MobileTimelineRow): boolean {
  return (row.clients?.length ?? 0) > 1;
}

function StatusBadge({ row }: { row: MobileTimelineRow }) {
  const theme = MOBILE_SCHEDULE_THEME[row.kind] ?? MOBILE_SCHEDULE_THEME.scheduled;

  switch (row.kind) {
    case "scheduled":
      return (
        <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", theme.badge)}>
          Agendada
        </span>
      );
    case "video":
      return (
        <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", theme.badge)}>
          Vídeo
        </span>
      );
    case "completed":
      return (
        <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", theme.badge)}>
          Realizada
        </span>
      );
    case "no_show":
      return (
        <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", theme.badge)}>
          Não apareceu
        </span>
      );
    case "post_visit_pending":
      return (
        <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", theme.badge)}>
          Sem status
        </span>
      );
    case "blocked":
      return (
        <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", theme.badge)}>
          Bloqueado
        </span>
      );
    case "free":
      return (
        <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", theme.badge)}>
          Livre
        </span>
      );
    default:
      return (
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
          Visita
        </span>
      );
  }
}

export function mobileScheduleCardStyle(
  row: MobileTimelineRow
): React.CSSProperties {
  const common: React.CSSProperties = {
    boxSizing: "border-box",
  };

  // Same palette as the desktop grid: upcoming visits are light green and completed
  // visits are solid dark green with white text, both with an even 1px border.
  switch (row.kind) {
    case "scheduled":
      return {
        ...common,
        border: "1px solid #2F855A",
        backgroundColor: "#DCF5E4",
      };
    case "video":
      return {
        ...common,
        border: "1px solid #3182CE",
        borderLeft: "4px solid #3182CE",
        backgroundColor: "#E1EEFB",
      };
    case "mixed":
      return {
        ...common,
        border: "1px solid #319795",
        borderLeft: "4px solid #319795",
        backgroundColor: "#DDF3F1",
      };
    case "completed":
      return {
        ...common,
        border: "1px solid #276749",
        backgroundColor: "#276749",
      };
    case "no_show":
      return {
        ...common,
        border: "1px solid #DD6B20",
        backgroundColor: "#FFFAF0",
      };
    case "post_visit_pending":
      return {
        ...common,
        border: "1px solid #D69E2E",
        backgroundColor: "#FFFAF0",
      };
    case "blocked":
      return {
        ...common,
        border: "1px solid #A0AEC0",
        backgroundColor: "#EDF2F7",
      };
    case "free":
      return {
        ...common,
        border: "1px dashed #A0AEC0",
        backgroundColor: "#FFFFFF",
      };
    default:
      return {
        ...common,
        border: "1px solid #E2E8F0",
        backgroundColor: "#FFFFFF",
      };
  }
}

function primaryTitle(row: MobileTimelineRow): string {
  if (row.kind === "free") {
    return "Horário livre";
  }
  if (row.kind === "blocked") {
    return row.blockedNote?.trim() || "Bloqueado";
  }
  if (row.kind === "video" && !row.clientLabel) {
    return "Visita por vídeo";
  }
  return row.clientLabel || "Visita";
}

function secondaryLine(row: MobileTimelineRow): string | null {
  if (row.kind === "video") {
    return "Link da chamada enviado";
  }
  if (row.kind === "post_visit_pending") {
    return "Marque como foi a visita";
  }
  if (row.clientBadge) {
    return `${row.clientBadge} clientes`;
  }
  return null;
}

type SlotCardProps = {
  row: MobileTimelineRow;
  /** Rendered above the title (the multi-visit badge and pager). */
  header?: React.ReactNode;
  className?: string;
  onPress?: () => void;
  onMarkOutcome: (outcome: ScheduleVisitOutcome) => Promise<void>;
};

function isVisitKind(kind: MobileTimelineRow["kind"]): boolean {
  return (
    kind === "scheduled" ||
    kind === "video" ||
    kind === "completed" ||
    kind === "no_show" ||
    kind === "post_visit_pending" ||
    kind === "mixed"
  );
}

/** The card of one slot with a single client (also each page of the multi-visit carousel). */
function SlotCard({ row, header, className, onPress, onMarkOutcome }: SlotCardProps) {
  const [submittingOutcome, setSubmittingOutcome] = useState<ScheduleVisitOutcome | null>(null);
  const isInteractive = onPress != null;

  const handleOutcome = async (outcome: ScheduleVisitOutcome) => {
    if (submittingOutcome) {
      return;
    }

    setSubmittingOutcome(outcome);
    try {
      await onMarkOutcome(outcome);
    } finally {
      setSubmittingOutcome(null);
    }
  };

  const showOutcomeActions =
    row.kind === "post_visit_pending" && AGENDA_FEATURE_FLAGS.postVisitPanelActions;

  return (
    <div
      style={mobileScheduleCardStyle(row)}
      className={cn(
        "min-h-[76px] w-full rounded-xl text-left transition",
        isInteractive && "hover:shadow-sm",
        className
      )}
    >
      {header}
      <button
        type="button"
        disabled={!isInteractive || submittingOutcome !== null}
        onClick={onPress}
        className={cn(
          "block w-full px-4 text-left",
          header ? "pt-1.5" : "pt-3.5",
          showOutcomeActions ? "pb-1" : "pb-3.5",
          isInteractive && "active:scale-[0.99]"
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              "min-w-0 flex-1 truncate text-base font-semibold",
              row.kind === "completed" ? "text-white" : "text-[#2D3748]"
            )}
          >
            {primaryTitle(row)}
          </p>
          <StatusBadge row={row} />
        </div>
        {secondaryLine(row) ? (
          <p
            className={cn(
              "mt-1 text-[13px]",
              // Darker on filled cards to keep the contrast readable.
              row.kind === "completed"
                ? "text-white/85"
                : row.kind === "scheduled" || row.kind === "video" || row.kind === "mixed"
                  ? "text-[#4A5568]"
                  : "text-[#718096]"
            )}
          >
            {secondaryLine(row)}
          </p>
        ) : null}
      </button>
      {showOutcomeActions ? (
        <div className="flex gap-2 px-4 pb-3.5 pt-2">
          <button
            type="button"
            disabled={submittingOutcome !== null}
            onClick={() => void handleOutcome("completed")}
            style={{ backgroundColor: "#15803D", color: "#FFFFFF" }}
            className="min-h-10 flex-1 rounded-lg px-3 text-sm font-semibold disabled:cursor-wait disabled:opacity-60"
          >
            {submittingOutcome === "completed" ? "Salvando..." : "Realizada"}
          </button>
          <button
            type="button"
            disabled={submittingOutcome !== null}
            onClick={() => void handleOutcome("no_show")}
            style={{
              backgroundColor: "#FFF7ED",
              border: "1px solid #F97316",
              color: "#9A3412",
            }}
            className="min-h-10 flex-1 rounded-lg px-3 text-sm font-semibold disabled:cursor-wait disabled:opacity-60"
          >
            {submittingOutcome === "no_show" ? "Salvando..." : "Não apareceu"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

type MultiVisitCarouselProps = {
  row: MobileTimelineRow;
  onPressVisit: (coord: string, scheduleId?: number) => void;
  onMarkOutcome: MobileScheduleItemProps["onMarkOutcome"];
};

/**
 * Several clients in the same hour: one full card per client, side by side. Swipe or the
 * arrows move to the next client; the badge shows which one (1/3).
 */
function MultiVisitCarousel({ row, onPressVisit, onMarkOutcome }: MultiVisitCarouselProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const count = row.clients.length;
  const [index, setIndex] = useState(() => initialClientSlideIndex(row.clients));
  const [height, setHeight] = useState<number | null>(null);

  // The track takes the height of the client on screen: a marked client stays compact and
  // the outcome buttons only add height when a pending client is shown.
  useLayoutEffect(() => {
    const slide = slideRefs.current[index];
    if (!slide) return;
    const sync = () => setHeight(slide.offsetHeight);
    sync();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(sync) : null;
    observer?.observe(slide);
    return () => observer?.disconnect();
  }, [index, row.clients]);

  // Start on the first client waiting for a result, without animation.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (track) {
      track.scrollLeft = index * track.clientWidth;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function goTo(nextIndex: number) {
    const track = trackRef.current;
    const target = Math.max(0, Math.min(count - 1, nextIndex));
    setIndex(target);
    track?.scrollTo({ left: target * track.clientWidth, behavior: "smooth" });
  }

  function handleScroll() {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const current = Math.round(track.scrollLeft / track.clientWidth);
    if (current !== index) setIndex(Math.max(0, Math.min(count - 1, current)));
  }

  return (
    <div
      ref={trackRef}
      onScroll={handleScroll}
      role="group"
      aria-roledescription="carrossel"
      aria-label={`Visita múltipla: ${count} clientes`}
      style={height != null ? { height } : undefined}
      className="flex snap-x snap-mandatory items-start overflow-x-auto overflow-y-hidden transition-[height] duration-200 ease-out [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {row.clients.map((client, clientIndex) => {
        const slide = buildClientSlideRow(row, client);
        const isDark = slide.kind === "completed";
        return (
          <div
            key={client.scheduleId}
            ref={(element) => {
              slideRefs.current[clientIndex] = element;
            }}
            className="w-full shrink-0 snap-center"
            aria-roledescription="slide"
            aria-label={`Cliente ${clientIndex + 1} de ${count}`}
          >
            <SlotCard
              row={slide}
              onPress={() => onPressVisit(row.coord, client.scheduleId)}
              onMarkOutcome={(outcome) => onMarkOutcome(row, outcome, [client.scheduleId])}
              header={
                <div className="flex items-center justify-between gap-2 px-4 pt-3">
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide",
                      isDark ? "bg-white/20 text-white" : "bg-[#2D3748] text-white"
                    )}
                  >
                    Visita múltipla
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => goTo(clientIndex - 1)}
                      disabled={clientIndex === 0}
                      aria-label="Cliente anterior"
                      className={cn(
                        "inline-flex h-7 w-7 items-center justify-center rounded-full border-0 disabled:opacity-30",
                        isDark ? "bg-white/15 text-white" : "bg-black/5 text-[#2D3748]"
                      )}
                    >
                      <ChevronLeft className="h-4 w-4" aria-hidden />
                    </button>
                    <span
                      className={cn(
                        "min-w-[2.5rem] text-center text-xs font-semibold tabular-nums",
                        isDark ? "text-white" : "text-[#4A5568]"
                      )}
                      aria-live="polite"
                    >
                      {clientIndex + 1}/{count}
                    </span>
                    <button
                      type="button"
                      onClick={() => goTo(clientIndex + 1)}
                      disabled={clientIndex === count - 1}
                      aria-label="Próximo cliente"
                      className={cn(
                        "inline-flex h-7 w-7 items-center justify-center rounded-full border-0 disabled:opacity-30",
                        isDark ? "bg-white/15 text-white" : "bg-black/5 text-[#2D3748]"
                      )}
                    >
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                </div>
              }
            />
          </div>
        );
      })}
    </div>
  );
}

const MobileScheduleItem: React.FC<MobileScheduleItemProps> = ({
  row,
  onPressVisit,
  onPressFreeSlot,
  onUnlockSlot,
  onMarkOutcome,
}) => {
  const isInteractive =
    row.kind === "free" || isVisitKind(row.kind) || (row.kind === "blocked" && onUnlockSlot != null);

  const handleClick = () => {
    if (row.kind === "free") {
      onPressFreeSlot(row.coord);
      return;
    }
    if (row.kind === "blocked") {
      onUnlockSlot?.(row.coord);
      return;
    }
    if (isVisitKind(row.kind)) {
      onPressVisit(row.coord, row.primaryScheduleId || undefined);
    }
  };

  return (
    <li className="grid grid-cols-[70px_minmax(0,1fr)] items-start gap-3">
      <time
        className="pt-3 text-[15px] font-semibold tabular-nums text-[#2D3748]"
        dateTime={row.hourLabel}
      >
        {row.hourLabel}
      </time>
      {isMultiVisit(row) ? (
        <MultiVisitCarousel row={row} onPressVisit={onPressVisit} onMarkOutcome={onMarkOutcome} />
      ) : (
        <SlotCard
          row={row}
          onPress={isInteractive ? handleClick : undefined}
          onMarkOutcome={(outcome) => onMarkOutcome(row, outcome)}
        />
      )}
    </li>
  );
};

export default MobileScheduleItem;
